// Historical Snapshots — unified archive of every saved snapshot across both
// streams (Monthly Rolling Projections + Projections vs Consumption).
//
// Reads via MongoDB only (`GET /api/historical-snapshots`) so the page keeps
// working even after the source Google Sheets are edited, rotated, or
// disconnected. Designed around three things the user asked for:
//
//   1. Grouped by Month/Year, with versions (v1, v2, v3 …) nested in a
//      collapsible group below each header.
//   2. View dialog renders three sections: Projection Summary · Channel
//      Summary · SKU Summary — each showing Projected / Sales / Dispatched.
//      Rolling-Projections snapshots have no Dispatched data; the columns
//      stay rendered for layout consistency but cells display "—".
//   3. View · Download Excel · Delete (admin-gated) per row.
//
// The Dispatched column behaviour is the most subtle bit. The data shape
// differs between the two snapshot kinds:
//   - "consumption" (Projections vs Consumption): `channels[]` and `rows[]`
//     each contain channel + projection + dispatched + sales.
//   - "rolling" (Monthly Rolling Projections): `rows[]` contains a
//     history[] array (actual sales months) + projections[] array
//     (forecast months). We derive a single "Projected" total per row
//     from `projections[0]` (the freshest projection month) and "Sales"
//     from `history[history.length - 1]` (the latest history month).
//     Dispatched stays "—".

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  RefreshCw, Archive, ChevronDown, ChevronRight, Eye, Download, Trash2,
  LockKeyhole, Layers,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { formatInt } from '@/lib/format-utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const KIND_LABEL = {
  rolling: 'Rolling Projections',
  consumption: 'Projections vs Consumption',
};
const KIND_BADGE = {
  rolling: 'bg-sky-100 text-sky-800 border-sky-300',
  consumption: 'bg-amber-100 text-amber-800 border-amber-300',
};

// ============ Derivation helpers ============
//
// `deriveSnapshotMetrics(detail)` returns a normalised shape used by the
// viewer regardless of which snapshot kind was loaded:
//   {
//     totals:   { projected, sales, dispatched },
//     channels: [{ channel, projected, sales, dispatched }, …],
//     rows:     [{ asin, brand, sku, projected, sales, dispatched }, …],
//     hasDispatched: bool,    // false for rolling-type snapshots
//   }
const sumChannelMap = (m = {}) =>
  Object.values(m || {}).reduce((s, v) => s + (Number(v) || 0), 0);

const deriveSnapshotMetrics = (detail) => {
  if (!detail) return null;
  const kind = detail.kind || (detail.channels ? 'consumption' : 'rolling');

  if (kind === 'consumption') {
    const channels = (detail.channels || []).map(c => ({
      channel:    c.channel,
      projected:  Number(c.projection)  || 0,
      sales:      Number(c.sales)       || 0,
      dispatched: Number(c.dispatched)  || 0,
    }));
    const rows = (detail.rows || []).map(r => ({
      asin:       r.asin  || '',
      brand:      r.brand || '',
      sku:        r.sku,
      projected:  Number(r.projected)  || 0,
      sales:      Number(r.sales)      || 0,
      dispatched: Number(r.dispatched) || 0,
    }));
    const totals = {
      projected:  rows.reduce((s, r) => s + r.projected,  0),
      sales:      rows.reduce((s, r) => s + r.sales,      0),
      dispatched: rows.reduce((s, r) => s + r.dispatched, 0),
    };
    return { totals, channels, rows, hasDispatched: true };
  }

  // rolling — derive from history[] and projections[]
  const channelsAcc = {}; // channel → { projected, sales }
  const rows = (detail.rows || []).map(r => {
    const proj0 = (r.projections || [])[0];
    const histN = (r.history || [])[(r.history?.length || 1) - 1];
    const rowProjected  = sumChannelMap(proj0?.channels);
    const rowSales      = sumChannelMap(histN?.channels);

    if (proj0?.channels) {
      Object.entries(proj0.channels).forEach(([ch, v]) => {
        if (!channelsAcc[ch]) channelsAcc[ch] = { projected: 0, sales: 0 };
        channelsAcc[ch].projected += Number(v) || 0;
      });
    }
    if (histN?.channels) {
      Object.entries(histN.channels).forEach(([ch, v]) => {
        if (!channelsAcc[ch]) channelsAcc[ch] = { projected: 0, sales: 0 };
        channelsAcc[ch].sales += Number(v) || 0;
      });
    }

    return {
      asin:       r.asin  || '',
      brand:      r.brand || '',
      sku:        r.sku,
      projected:  rowProjected,
      sales:      rowSales,
      dispatched: null,
    };
  });

  const channels = Object.entries(channelsAcc).map(([ch, vals]) => ({
    channel:    ch,
    projected:  vals.projected,
    sales:      vals.sales,
    dispatched: null,
  }));
  const totals = {
    projected:  rows.reduce((s, r) => s + r.projected, 0),
    sales:      rows.reduce((s, r) => s + r.sales,     0),
    dispatched: null,
  };
  return { totals, channels, rows, hasDispatched: false };
};

const fmtDispatched = (v) => (v == null ? '—' : formatInt(v));

export default function HistoricalSnapshotsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [viewing, setViewing] = useState(null);          // resolved detail doc
  const [viewKind, setViewKind] = useState(null);
  const [filterKind, setFilterKind] = useState('all');   // 'all' | 'rolling' | 'consumption'

  // Admin password — reused from Allocation Planner / Rolling Projections.
  const [adminUnlocked, setAdminUnlocked] = useState(
    () => sessionStorage.getItem('editor_unlocked') === '1',
  );
  const [adminPwInMem, setAdminPwInMem] = useState(
    () => sessionStorage.getItem('editor_pw') || '',
  );
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null); // { id, kind }

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('radar_session_token')}`,
  });

  useEffect(() => {
    const token = localStorage.getItem('radar_session_token');
    if (!token) { navigate('/'); return; }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/historical-snapshots`, { headers: authHeaders() });
      setItems(res.data.snapshots || []);
    } catch (e) {
      toast.error('Failed to load snapshots');
    } finally {
      setLoading(false);
    }
  };

  // ============ Group by (year, month) keeping versions desc inside each ============
  const groups = useMemo(() => {
    const filtered = filterKind === 'all'
      ? items
      : items.filter(s => s.kind === filterKind);
    const acc = new Map();
    filtered.forEach(s => {
      const key = `${s.projection_year}-${s.projection_month}`;
      if (!acc.has(key)) {
        acc.set(key, {
          key,
          year: s.projection_year,
          month: s.projection_month,
          label: `${MONTHS[s.projection_month]} ${s.projection_year}`,
          versions: [],
        });
      }
      acc.get(key).versions.push(s);
    });
    // sort versions desc within group
    [...acc.values()].forEach(g => {
      g.versions.sort((a, b) => (b.version || 0) - (a.version || 0));
    });
    // sort groups newest first
    return [...acc.values()].sort(
      (a, b) => (b.year - a.year) || (b.month - a.month),
    );
  }, [items, filterKind]);

  // Auto-open the freshest group whenever the data changes so users land on
  // something useful without having to expand manually.
  useEffect(() => {
    if (groups.length === 0) return;
    setOpenGroups(prev => (prev[groups[0].key] === undefined
      ? { ...prev, [groups[0].key]: true }
      : prev));
  }, [groups]);

  const toggleGroup = (key) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ============ View / Download / Delete handlers ============
  const detailPathFor = (kind, id) =>
    `${API}/${kind === 'rolling' ? 'rolling-projections' : 'projections-consumption'}/snapshots/${id}`;

  const handleView = async (snap) => {
    try {
      const res = await axios.get(detailPathFor(snap.kind, snap.id), { headers: authHeaders() });
      setViewing({ ...res.data, kind: snap.kind });
      setViewKind(snap.kind);
    } catch (e) {
      toast.error('Failed to load snapshot');
    }
  };

  const handleDownload = async (snap) => {
    let detail = snap;
    if (!snap.rows && !snap.channels) {
      const res = await axios.get(detailPathFor(snap.kind, snap.id), { headers: authHeaders() });
      detail = res.data;
    }
    downloadSnapshot(snap.kind, detail);
  };

  const handleAttemptDelete = (snap) => {
    if (!adminUnlocked) {
      setPendingDelete({ id: snap.id, kind: snap.kind });
      setPwDialogOpen(true);
      return;
    }
    runDelete(snap.id, snap.kind, adminPwInMem);
  };

  const runDelete = async (id, kind, password) => {
    try {
      await axios.delete(detailPathFor(kind, id), {
        headers: { ...authHeaders(), 'X-Editor-Password': password },
      });
      toast.success('Snapshot deleted');
      setItems(prev => prev.filter(s => s.id !== id));
      if (viewing?.id === id) { setViewing(null); setViewKind(null); }
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem('editor_unlocked');
        sessionStorage.removeItem('editor_pw');
        setAdminUnlocked(false);
        setAdminPwInMem('');
        toast.error('Admin password expired — please re-enter');
        setPwDialogOpen(true);
      } else {
        toast.error(e.response?.data?.detail || 'Failed to delete snapshot');
      }
    }
  };

  const handlePwSubmit = async () => {
    if (!pwInput.trim()) return;
    try {
      await axios.post(`${API}/auth/editor-unlock`,
        { password: pwInput }, { headers: authHeaders() });
      sessionStorage.setItem('editor_unlocked', '1');
      sessionStorage.setItem('editor_pw', pwInput);
      setAdminUnlocked(true);
      setAdminPwInMem(pwInput);
      setPwDialogOpen(false);
      const pending = pendingDelete;
      const pw = pwInput;
      setPwInput('');
      setPendingDelete(null);
      if (pending) runDelete(pending.id, pending.kind, pw);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid admin password');
    }
  };

  // ============ Download (3-sheet workbook, kind-aware) ============
  const downloadSnapshot = (kind, snap) => {
    const m = deriveSnapshotMetrics({ ...snap, kind });
    if (!m) return;
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Projection Summary (totals).
    const summaryAoa = [
      ['', 'Projected Units', 'Sales Units', 'Dispatched Units'],
      ['Total',
        m.totals.projected,
        m.totals.sales,
        m.hasDispatched ? m.totals.dispatched : '—',
      ],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryAoa);
    wsSummary['!cols'] = [{ wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Projection Summary');

    // Sheet 2 — Channel Summary.
    const chAoa = [
      ['Channel', 'Projected', 'Sales', 'Dispatched'],
      ...m.channels.map(c => [
        c.channel, c.projected, c.sales,
        m.hasDispatched ? c.dispatched : '—',
      ]),
    ];
    const wsCh = XLSX.utils.aoa_to_sheet(chAoa);
    wsCh['!cols'] = [{ wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsCh, 'Channel Summary');

    // Sheet 3 — SKU Summary.
    const skuAoa = [
      ['ASIN', 'Brand', 'SKU', 'Projected', 'Sales', 'Dispatched'],
      ...m.rows.map(r => [
        r.asin || '', r.brand || '', r.sku,
        r.projected, r.sales,
        m.hasDispatched ? r.dispatched : '—',
      ]),
    ];
    const wsSku = XLSX.utils.aoa_to_sheet(skuAoa);
    wsSku['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsSku, 'SKU Summary');

    // Sheet 4 — Metadata.
    const meta = [
      ['Type',             KIND_LABEL[kind]],
      ['Projection Month', MONTHS[snap.projection_month]],
      ['Projection Year',  snap.projection_year],
      ['Version',          snap.version ?? 1],
      ['Saved At',         snap.save_timestamp],
      ['Saved By',         snap.user_id || '—'],
      ['Channels',         m.channels.length],
      ['SKU Rows',         m.rows.length],
    ];
    const wsMeta = XLSX.utils.aoa_to_sheet(meta);
    wsMeta['!cols'] = [{ wch: 20 }, { wch: 36 }];
    XLSX.utils.book_append_sheet(wb, wsMeta, 'Snapshot Info');

    const filename = `${kind === 'rolling' ? 'Rolling' : 'Consumption'}_Snapshot_${MONTHS[snap.projection_month]}${snap.projection_year}_v${snap.version ?? 1}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success('Snapshot downloaded');
  };

  // ============ View metrics (memoised so dialog open is cheap) ============
  const viewMetrics = useMemo(
    () => (viewing ? deriveSnapshotMetrics(viewing) : null),
    [viewing],
  );

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-[1900px] mx-auto p-4 md:p-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-wv-ink">
              Historical Snapshots
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Unified archive · Rolling Projections + Projections vs Consumption · loaded from MongoDB only.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Kind filter pills */}
            <div className="flex items-center gap-1 mr-2">
              {[
                { key: 'all',          label: 'All' },
                { key: 'rolling',      label: 'Rolling' },
                { key: 'consumption',  label: 'Consumption' },
              ].map(b => (
                <button
                  key={b.key}
                  onClick={() => setFilterKind(b.key)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    filterKind === b.key
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-white text-wv-body border-border hover:border-primary/60'
                  }`}
                  data-testid={`hist-filter-${b.key}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
            <Button onClick={fetchAll} disabled={loading} data-testid="hist-refresh-btn">
              <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={16} />
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1900px] mx-auto p-4 md:p-8 space-y-4">
        {groups.length === 0 ? (
          <Card className="p-12 text-center" data-testid="hist-empty">
            <Layers size={32} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {loading
                ? 'Loading snapshots…'
                : 'No snapshots saved yet. Use the Save Historical Data button on Monthly Rolling Projections or Projections vs Consumption to capture one.'}
            </p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="hist-groups">
            {groups.map(g => {
              const isOpen = !!openGroups[g.key];
              const Chevron = isOpen ? ChevronDown : ChevronRight;
              const kindsHere = [...new Set(g.versions.map(v => v.kind))];
              return (
                <Card key={g.key} className="overflow-hidden" data-testid={`hist-group-${g.key}`}>
                  <button
                    onClick={() => toggleGroup(g.key)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
                    data-testid={`hist-group-toggle-${g.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <Chevron size={16} className="text-muted-foreground" />
                      <span className="font-semibold text-wv-ink">{g.label}</span>
                      <span className="text-xs text-muted-foreground">
                        ({g.versions.length} version{g.versions.length === 1 ? '' : 's'})
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {kindsHere.map(k => (
                        <span
                          key={k}
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase border ${KIND_BADGE[k]}`}
                        >
                          {KIND_LABEL[k]}
                        </span>
                      ))}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-border">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/30 text-muted-foreground">
                          <tr>
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest">Version</th>
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest">Type</th>
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest">Saved At</th>
                            <th className="text-right p-2 text-[10px] uppercase tracking-widest">Rows</th>
                            <th className="text-right p-2 text-[10px] uppercase tracking-widest">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.versions.map((s, i) => (
                            <tr
                              key={s.id}
                              className={`${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-t border-border/60`}
                              data-testid={`hist-row-${s.id}`}
                            >
                              <td className="p-2 text-xs">
                                <span className="inline-block px-2 py-0.5 rounded bg-primary/10 text-primary font-semibold text-[11px]"
                                  data-testid={`hist-version-${s.id}`}>
                                  v{s.version ?? 1}
                                </span>
                              </td>
                              <td className="p-2 text-xs">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${KIND_BADGE[s.kind]}`}>
                                  {KIND_LABEL[s.kind]}
                                </span>
                              </td>
                              <td className="p-2 text-xs text-wv-body">
                                {new Date(s.save_timestamp).toLocaleString()}
                              </td>
                              <td className="p-2 text-xs text-right tabular-nums">
                                {s.row_count ?? '—'}
                              </td>
                              <td className="p-2 text-right">
                                <div className="flex items-center justify-end gap-1.5">
                                  <Button size="sm" variant="outline" className="h-7 px-2"
                                    onClick={() => handleView(s)}
                                    data-testid={`hist-view-${s.id}`}>
                                    <Eye size={12} className="mr-1" />View
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 px-2"
                                    onClick={() => handleDownload(s)}
                                    data-testid={`hist-download-${s.id}`}>
                                    <Download size={12} className="mr-1" />Excel
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 px-2 border-rose-500 text-rose-600 hover:bg-rose-50"
                                    onClick={() => handleAttemptDelete(s)}
                                    data-testid={`hist-delete-${s.id}`}
                                    title="Admin only">
                                    <Trash2 size={12} className="mr-1" />Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* ============ Viewer: Projection · Channel · SKU summaries ============ */}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) { setViewing(null); setViewKind(null); } }}>
        <DialogContent className="max-w-6xl" data-testid="hist-viewer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive size={18} />
              {viewing && (
                <>
                  {MONTHS[viewing.projection_month]} {viewing.projection_year}
                  {viewing.version != null && ` · v${viewing.version}`}
                  {viewKind && (
                    <span className={`ml-2 px-2 py-0.5 rounded text-[10px] font-semibold uppercase border ${KIND_BADGE[viewKind]}`}>
                      {KIND_LABEL[viewKind]}
                    </span>
                  )}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewing && viewMetrics && (
            <div className="space-y-5 max-h-[72vh] overflow-auto pr-1">
              <div className="text-xs text-muted-foreground">
                Saved {new Date(viewing.save_timestamp).toLocaleString()} ·
                User <code className="text-[10px] bg-slate-100 px-1 rounded">{viewing.user_id}</code>
                {viewing.start_date && ` · Window ${viewing.start_date} → ${viewing.end_date}`}
                {!viewMetrics.hasDispatched && ' · Dispatched not available for Rolling Projections snapshots'}
              </div>

              {/* Projection Summary */}
              <section data-testid="hist-section-projection-summary">
                <h3 className="text-sm font-semibold text-wv-ink mb-2">Projection Summary</h3>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Projected Units',  value: formatInt(viewMetrics.totals.projected),  tone: 'border-sky-300' },
                    { label: 'Sales Units',       value: formatInt(viewMetrics.totals.sales),       tone: 'border-emerald-300' },
                    { label: 'Dispatched Units',  value: fmtDispatched(viewMetrics.totals.dispatched), tone: 'border-amber-300' },
                  ].map(c => (
                    <Card key={c.label} className={`p-4 bg-white border-2 ${c.tone}`}>
                      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{c.label}</p>
                      <p className="text-2xl font-semibold tracking-tight tabular-nums text-wv-ink mt-1"
                        data-testid={`hist-summary-${c.label.replace(/\s+/g,'-').toLowerCase()}`}>
                        {c.value}
                      </p>
                    </Card>
                  ))}
                </div>
              </section>

              {/* Channel Summary */}
              <section data-testid="hist-section-channel-summary">
                <h3 className="text-sm font-semibold text-wv-ink mb-2">Channel Summary</h3>
                <div className="border border-border rounded overflow-auto">
                  <table className="w-full text-xs" data-testid="hist-channel-table">
                    <thead className="bg-primary text-primary-foreground">
                      <tr>
                        <th className="p-2 text-left uppercase tracking-widest text-[10px]">Channel</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Projected</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Sales</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Dispatched</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewMetrics.channels.map((c, i) => (
                        <tr key={c.channel} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="p-2 text-wv-ink">{c.channel}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(c.projected)}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(c.sales)}</td>
                          <td className="p-2 text-right tabular-nums">{viewMetrics.hasDispatched ? formatInt(c.dispatched) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* SKU Summary */}
              <section data-testid="hist-section-sku-summary">
                <h3 className="text-sm font-semibold text-wv-ink mb-2">SKU Summary ({viewMetrics.rows.length})</h3>
                <div className="border border-border rounded overflow-auto" style={{ maxHeight: '42vh' }}>
                  <table className="w-full text-xs" data-testid="hist-sku-table">
                    <thead className="bg-primary text-primary-foreground sticky top-0">
                      <tr>
                        <th className="p-2 text-left uppercase tracking-widest text-[10px]">ASIN</th>
                        <th className="p-2 text-left uppercase tracking-widest text-[10px]">Brand</th>
                        <th className="p-2 text-left uppercase tracking-widest text-[10px]">SKU</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Projected</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Sales</th>
                        <th className="p-2 text-right uppercase tracking-widest text-[10px]">Dispatched</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewMetrics.rows.map((r, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="p-2 text-wv-ink whitespace-nowrap">{r.asin || '—'}</td>
                          <td className="p-2 text-wv-body whitespace-nowrap">{r.brand || '—'}</td>
                          <td className="p-2 text-wv-ink">{r.sku}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(r.projected)}</td>
                          <td className="p-2 text-right tabular-nums">{formatInt(r.sales)}</td>
                          <td className="p-2 text-right tabular-nums">{viewMetrics.hasDispatched ? formatInt(r.dispatched) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline"
              onClick={() => viewing && downloadSnapshot(viewKind, viewing)}
              disabled={!viewing}
              data-testid="hist-viewer-download">
              <Download size={14} className="mr-2" /> Download Excel
            </Button>
            <Button variant="outline" onClick={() => { setViewing(null); setViewKind(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin password gate for Delete. */}
      <Dialog open={pwDialogOpen} onOpenChange={(o) => { if (!o) { setPwDialogOpen(false); setPwInput(''); setPendingDelete(null); } }}>
        <DialogContent className="max-w-sm" data-testid="hist-admin-pw-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LockKeyhole size={18} /> Admin Password Required</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Admin Password</Label>
            <Input
              type="password" autoFocus value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handlePwSubmit(); }}
              data-testid="hist-admin-pw-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setPwDialogOpen(false); setPwInput(''); setPendingDelete(null); }}>Cancel</Button>
            <Button size="sm" onClick={handlePwSubmit} disabled={!pwInput.trim()} data-testid="hist-admin-pw-submit">Unlock & Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
